import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import './LandingPage.css';

function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="stadtki-page">
      <div className="stadtki-thread" aria-hidden="true" />

      <div className="stadtki-inner">
        <div className="stadtki-content">
          <header className="stadtki-header">
            <div className="stadtki-logo-wrap">
              <img
                src="/logo-stadtki.jpg"
                alt="StadtKI Logo"
                className="stadtki-logo"
              />
              <div className="stadtki-logo-text">stadtki</div>
            </div>

            <div className="stadtki-header-cta">
              <nav className="stadtki-nav" aria-label="Hauptnavigation">
                <a href="#loesung">Lösung</a>
                <a href="#sicherheit">Sicherheit</a>
                <a href="#ueber-uns">Über uns</a>
              </nav>
              <Button
                variant="outline"
                onClick={() => navigate('/login')}
                className="btn-glow"
                style={{ marginLeft: '0.5rem' }}
              >
                <LogIn className="w-4 h-4 mr-2" />
                Login
              </Button>
              <a href="#kontakt" className="btn btn-primary">
                Demo anfordern
              </a>
            </div>
          </header>

          <main>
            <section className="hero" aria-labelledby="hero-title">
              <div className="hero-text">
                <div className="tagline">
                  KI‑Plattform für Städte, Gemeinden und Behörden
                </div>
                <h1 id="hero-title">Das Betriebssystem für Ihre Bürgerkommunikation</h1>
                <p>Entlastet Ihr Amt von Routinefragen.</p>
                <p>DSGVO-konform, rechtssicher und rund um die Uhr verfügbar.</p>
                <p>
                  Klassische Suchfunktionen auf kommunalen Webseiten frustrieren Bürger –
                  StadtKI macht Antworten wirklich auffindbar.
                </p>

                <div
                  style={{
                    display: 'flex',
                    gap: '2rem',
                    marginTop: '2.5rem',
                    marginBottom: '1.75rem',
                  }}
                >
                  <a href="#kontakt" className="btn btn-cta">
                    Jetzt Pilotprojekt starten
                  </a>
                  <a href="#loesung" className="btn" style={{ paddingInline: '1.1rem' }}>
                    Mehr zur Lösung
                  </a>
                </div>

              </div>

              <div className="hero-visual" aria-hidden="true">
                <div className="hero-video-frame">
                  <div className="hero-video-glow" />
                  <div className="hero-video-shell">
                    <video
                      className="hero-video"
                      src="/stadtki-hero.mp4"
                      autoPlay
                      muted
                      loop
                      playsInline
                    />
                  </div>
                </div>
              </div>
            </section>

            <section className="trust-bar" aria-label="Vertrauensmerkmale">
              <div className="trust-items">
                <div className="trust-pill">Serverstandort Deutschland</div>
                <div className="trust-pill">Made in Germany</div>
                <div className="trust-pill">DSGVO-konform</div>
                <div className="trust-pill">Barrierefrei nach BITV</div>
              </div>
            </section>

            <section id="loesung" className="section">
              <div className="section-header">
                <div className="section-eyebrow">Wie StadtKI arbeitet</div>
                <h2 className="section-title">Von der Anfrage zur rechtssicheren Antwort.</h2>
                <p className="section-subtitle">
                  StadtKI indexiert Ihre bestehenden Dokumente und Satzungen und
                  beantwortet Bürgeranfragen so, wie es Ihre Mitarbeitenden tun würden –
                  nur schneller, skalierbar und dokumentiert.
                </p>
              </div>

              <div className="feature-grid">
                <article className="feature-card">
                  <div className="feature-node" />
                  <h3>Die Wissensdatenbank</h3>
                  <p>Indexiert Ihre PDFs, Satzungen und Formulare – ohne sie zu verändern.</p>
                </article>

                <article className="feature-card">
                  <div className="feature-node" />
                  <h3>Der Sicherheits‑Check</h3>
                  <p>
                    Antwortet nur mit geprüften Textbausteinen inkl. Quellenangabe – für
                    Nachvollziehbarkeit gegenüber Revisionsstellen.
                  </p>
                </article>

                <article className="feature-card">
                  <div className="feature-node" />
                  <h3>Die Integration</h3>
                  <p>
                    Läuft als Widget auf Ihrer bestehenden Webseite – ohne Eingriff in Ihr
                    Fachverfahren.
                  </p>
                </article>
              </div>
            </section>

            <section id="sicherheit" className="section">
              <div className="section-header">
                <div className="section-eyebrow">Sicherheit & Governance</div>
                <h2 className="section-title">Für Amtsleiter gebaut – nicht für Marketing.</h2>
                <p className="section-subtitle">
                  Datenschutz, Protokollierung und klare Verantwortlichkeiten sind ab
                  Tag eins mitgedacht. StadtKI ist kein Experiment, sondern ein
                  Betriebswerkzeug.
                </p>
              </div>
            </section>

            <section id="ueber-uns" className="about">
              <div>
                <div className="about-photo">
                  <img
                    src="/consultant.jpg"
                    alt="Berater hinter StadtKI"
                  />
                </div>
              </div>
              <div className="about-text">
                <div className="section-eyebrow">Über StadtKI</div>
                <h2>Wir sind keine Agentur. Wir sind Ingenieure.</h2>
                <p>
                  Hinter StadtKI stehen Ingenieure mit jahrzehntelanger Erfahrung in
                  sicherheitskritischen Systemen.
                </p>
                <p>
                  Mehr als 30 Patente und Projekte bei der BSH – Bosch Hausgeräte sind
                  unsere Garantie für robuste, ausfallsichere Lösungen.
                </p>
                <div className="about-highlight">
                  Wir sprechen die Sprache der Verwaltung – und übersetzen KI in
                  belastbare Prozesse, die im Alltag bestehen.
                </div>
              </div>
            </section>

            <footer id="kontakt" className="footer">
              <div className="footer-inner">
                <div className="footer-logo">
                  <div className="footer-logo-mark">
                    <div className="footer-logo-text-main">stadtki</div>
                    <div className="footer-logo-subline">
                      Die KI‑Schicht für Ihre Bürgerkommunikation
                    </div>
                    <img src="/logo-stadtki-negative.png?v=4" alt="StadtKI Logo negativ" />
                  </div>
                  <div className="footer-copy">
                    StadtKI verbindet Bürger und Amt über einen digitalen Faden – von der
                    Anfrage bis zur dokumentierten Antwort.
                  </div>
                </div>

                <div className="footer-contact">
                  <h2>Pilotprojekt anfragen</h2>
                  <p>
                    Hinterlassen Sie uns ein paar Eckdaten. Wir melden uns persönlich mit
                    einem Vorschlag für ein Pilotprojekt in Ihrer Verwaltung.
                  </p>
                  <form
                    className="contact-form"
                    action="/contact.php"
                    method="POST"
                  >
                    <div className="field-group">
                      <label className="field-label" htmlFor="behoerde">
                        Behörde / Kommune
                      </label>
                      <input
                        className="field-input"
                        id="behoerde"
                        name="behoerde"
                        placeholder="z. B. Stadt Musterhausen"
                      />
                    </div>
                    <div className="field-group">
                      <label className="field-label" htmlFor="email">
                        Dienstliche E‑Mail
                      </label>
                      <input
                        className="field-input"
                        id="email"
                        name="email"
                        type="email"
                        placeholder="name@behörde.de"
                      />
                    </div>
                    <div className="field-group">
                      <label className="field-label" htmlFor="nachricht">
                        Anliegen
                      </label>
                      <textarea
                        className="field-textarea"
                        id="nachricht"
                        name="nachricht"
                        placeholder="Welche Prozesse oder Fachbereiche möchten Sie entlasten?"
                      />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                      <button type="submit" className="btn btn-primary">
                        Pilotprojekt senden
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              <div className="footer-bottom">
                <span>© {new Date().getFullYear()} StadtKI. Alle Rechte vorbehalten.</span>
                <div className="footer-bottom-links">
                  <span>stadtki ist ein Produkt der baimaisai GmbH</span>
                  <span>·</span>
                  <a href="/datenschutz.html">Datenschutz</a>
                  <span>·</span>
                  <a href="/nutzungsbedingungen.html">Nutzungsbedingungen</a>
                  <span>·</span>
                  <a href="/impressum.html">Impressum</a>
                </div>
              </div>
            </footer>
          </main>
        </div>
      </div>
    </div>
  );
}

export default LandingPage;

